import { UpsertForm } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { TextList } from "@/components/text-list";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useGetGuides } from "@/hooks/guides";
import { useGetAllTourAgencies } from "@/hooks/tour-agencies";
import { useGetWorkers } from "@/hooks/workers";
import {
  CreateReservation,
  reservationRegionAsArray,
  ReservationState,
  reservationStatesAsArray,
  ReservationTags,
  reservationTagsAsArray,
  TourStandard,
  tourStandardAsArray,
} from "@/models";
import { useForm } from "@deadcow-enterprises/react-forms";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useReservationFormContext } from "./contex";

type CreateReservationWithoutGroupLeader = Omit<
  CreateReservation,
  "groupLeaderId"
>;

export function ReservationTab() {
  const {
    setReservation,
    reservation,
    isEdit,
    setReservationValidator,
    isDirty,
  } = useReservationFormContext();
  const { data: tourAgencies, isLoading: tourAgenciesLoading } =
    useGetAllTourAgencies();
  const { data: guides, isLoading: guidesLoading } = useGetGuides();
  const { data: workers, isLoading: workersLoading } = useGetWorkers();

  const { t } = useTranslation();

  const form = useForm<CreateReservationWithoutGroupLeader>({
    begin: {
      value: reservation?.begin,
      validator: z.date({
        required_error: "errors.required",
        invalid_type_error: "errors.dateFormat",
      }),
    },
    end: {
      value: reservation?.end,
    },
    agencyReservationNumber: {
      value: reservation?.agencyReservationNumber,
    },
    balance: {
      value: reservation?.balance,
    },
    bikeCount: {
      value: reservation?.bikeCount,
    },
    details: {
      value: reservation?.details,
    },
    number: {
      value: reservation?.number,
    },
    workerId: {
      value: reservation?.workerId,
    },
    customLinks: {
      value: reservation?.customLinks,
    },
    expensesMap: {
      value: reservation?.expensesMap,
    },
    headCount: {
      value: reservation?.headCount,
    },
    operationRegion: {
      value: reservation?.operationRegion,
    },
    ownerId: {
      value: reservation?.ownerId,
    },
    paymentDate: {
      value: reservation?.paymentDate,
    },
    price: {
      value: reservation?.price,
    },
    rwgpslinks: {
      value: reservation?.rwgpslinks,
    },
    rwgpsrequired: {
      value: reservation?.rwgpsrequired,
    },
    state: {
      value: reservation?.state ?? ReservationState.Pending,
    },
    tfylinks: {
      value: reservation?.tfylinks,
    },
    tourAgencyId: {
      value: reservation?.tourAgencyId,
    },
    tourLeaderId: {
      value: reservation?.tourLeaderId,
    },
    tourStandart: {
      value: reservation?.tourStandart ?? TourStandard.Standard,
    },
    travefyRequired: {
      value: reservation?.travefyRequired,
    },
    travefySent: {
      value: reservation?.travefySent ?? ReservationTags.No,
    },
    rwgpsSent: {
      value: reservation?.rwgpsSent ?? ReservationTags.No,
    },
    gpxSent: {
      value: reservation?.gpxSent ?? ReservationTags.No,
    },
    gpxRequired: {
      value: reservation?.gpxRequired ?? false,
    },
    gpxlinks: {
      value: reservation?.gpxlinks,
    },
    feedbackRequired: {
      value: reservation?.feedbackRequired ?? false,
    },
    feedbackLinks: {
      value: reservation?.feedbackLinks,
    },
    dropboxRequired: {
      value: reservation?.dropboxRequired ?? false,
    },
    dropboxLinks: {
      value: reservation?.dropboxLinks,
    },
    documentationSentDate: {
      value: reservation?.documentationSentDate,
    },
    bookingDate: {
      value: reservation?.bookingDate,
    },
    confirmedDate: {
      value: reservation?.confirmedDate,
    },
  });

  useEffect(() => {
    setReservationValidator(() => form.updateValidity());
  }, [form, setReservationValidator]);

  useEffect(() => {
    if (isDirty && !form.dirty) {
      form.updateValidity();
      form.markAsDirty();
    }
  }, [form, isDirty]);

  useEffect(() => {
    setReservation(form.value, form.dirty);
  }, [form.dirty, form.value, setReservation]);

  return (
    <UpsertForm
      form={form}
      onUpsert={async () => void 0}
      className="mx-auto max-w-3xl"
      fields={[
        {
          key: "tourAgencyId",
          label: "reservations.fields.tourAgencyId",
          input: (_, id) =>
            tourAgenciesLoading || !tourAgencies ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                disabled={isEdit}
                className="w-full"
                value={form.value.tourAgencyId?.toString()}
                onChange={(taId) => {
                  if (isEdit) return;
                  form.setValue("tourAgencyId", Number.parseInt(taId));

                  if (typeof form.value.tourLeaderId === "number") return;

                  const ta = tourAgencies?.find((ta) => ta.id === taId);
                  form.setValue("tourLeaderId", ta?.tour?.tourLeaderId);
                  form.setValue("operationRegion", ta?.tour?.operationRegion);
                }}
                options={tourAgencies.map((ta) => ({
                  label: ta.tour?.name ?? "-",
                  description: ta.agency?.name ?? "-",
                  value: ta.id,
                }))}
              />
            ),
        },
        {
          key: "tourLeaderId",
          label: "reservations.fields.tourLeaderId",
          input: (_, id) =>
            guidesLoading || !guides ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                className="w-full"
                value={form.value.tourLeaderId}
                onChange={(v) => {
                  form.setValue("tourLeaderId", v);
                }}
                options={guides.map((g) => ({
                  label: g.name,
                  value: Number.parseInt(g.id),
                }))}
              />
            ),
        },
        {
          key: "operationRegion",
          label: "reservations.fields.operationRegion",
          input: (_, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.operationRegion}
              onChange={(v) => {
                form.setValue("operationRegion", v);
              }}
              options={reservationRegionAsArray.map((or) => ({
                value: or,
                label: t(`common.operationRegions.${or}`),
              }))}
            />
          ),
        },
        {
          key: "begin",
          label: "reservations.fields.begin",
          input: (_, id) => (
            <DatePicker
              className="w-full"
              id={id}
              date={form.value.begin ?? undefined}
              setDate={(date) => {
                form.setValue("begin", date);
              }}
            />
          ),
        },
        {
          key: "agencyReservationNumber",
          label: "reservations.fields.agencyReservationNumber",
          input: (_, id) => (
            <Input
              id={id}
              value={form.value.agencyReservationNumber ?? ""}
              onChange={(e) => {
                form.setValue("agencyReservationNumber", e.target.value);
              }}
            />
          ),
        },
        {
          key: "workerId",
          label: "reservations.fields.workerId",
          input: (_, id) =>
            workersLoading || !workers ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                className="w-full"
                value={form.value.workerId}
                onChange={(v) => {
                  form.setValue("workerId", v);
                }}
                options={workers.map((w) => ({
                  label: w.name,
                  value: Number.parseInt(w.id),
                }))}
              />
            ),
        },
        {
          key: "documentationSentDate",
          label: "reservations.fields.documentationSentDate",
          input: (_, id) => (
            <DatePicker
              className="w-full"
              id={id}
              date={form.value.documentationSentDate ?? undefined}
              setDate={(date) => {
                form.setValue("documentationSentDate", date);
              }}
            />
          ),
        },
        {
          key: "bookingDate",
          label: "reservations.fields.bookingDate",
          input: (_, id) => (
            <DatePicker
              className="w-full"
              id={id}
              date={form.value.bookingDate ?? undefined}
              setDate={(date) => {
                form.setValue("bookingDate", date);
              }}
            />
          ),
        },
        {
          key: "confirmedDate",
          label: "reservations.fields.confirmedDate",
          input: (_, id) => (
            <DatePicker
              className="w-full"
              id={id}
              date={form.value.confirmedDate ?? undefined}
              setDate={(date) => {
                form.setValue("confirmedDate", date);
              }}
            />
          ),
        },
        {
          key: "state",
          label: "reservations.fields.state",
          input: (_, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.state}
              onChange={(v) => {
                form.setValue("state", v);
              }}
              options={reservationStatesAsArray.map((rs) => ({
                value: rs,
                label: t(`reservations.state.${rs}`),
              }))}
            />
          ),
        },
        {
          key: "tourStandart",
          label: "reservations.fields.tourStandart",
          input: (_, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.tourStandart}
              onChange={(v) => {
                form.setValue("tourStandart", v);
              }}
              options={tourStandardAsArray.map((ts) => ({
                value: ts,
                label: t(`reservations.tourStandard.${ts}`),
              }))}
            />
          ),
        },
        {
          key: "details",
          label: "reservations.fields.details",
          input: (_, id) => (
            <Textarea
              id={id}
              value={form.value.details ?? ""}
              onChange={(e) => {
                form.setValue("details", e.target.value);
              }}
            />
          ),
        },
        {
          key: "rwgpsrequired",
          label: "reservations.fields.rwgpsrequired",
          orientation: "horizontal",
          input: (_, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("rwgpsrequired", v);
              }}
              checked={!!form.value.rwgpsrequired}
            />
          ),
        },
        {
          visible: (f) => !!f.value.rwgpsrequired,
          key: "rwgpslinks",
          label: "reservations.fields.rwgpslinks",
          input: (_, id) => (
            <TextList
              id={id}
              onChange={(v) => form.setValue("rwgpslinks", v)}
              value={form.value.rwgpslinks ?? []}
              className="w-full"
            />
          ),
        },
        {
          key: "travefyRequired",
          label: "reservations.fields.travefyRequired",
          orientation: "horizontal",
          input: (_, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("travefyRequired", v);
              }}
              checked={!!form.value.travefyRequired}
            />
          ),
        },
        {
          visible: (f) => !!f.value.travefyRequired,
          key: "tfylinks",
          label: "reservations.fields.tfylinks",
          input: (_, id) => (
            <TextList
              id={id}
              onChange={(v) => form.setValue("tfylinks", v)}
              value={form.value.tfylinks ?? []}
              className="w-full"
            />
          ),
        },
        {
          key: "gpxRequired",
          label: "reservations.fields.gpxRequired",
          orientation: "horizontal",
          input: (_, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("gpxRequired", v);
              }}
              checked={!!form.value.gpxRequired}
            />
          ),
        },
        {
          visible: (f) => !!f.value.gpxRequired,
          key: "gpxlinks",
          label: "reservations.fields.gpxlinks",
          input: (_, id) => (
            <TextList
              id={id}
              onChange={(v) => form.setValue("gpxlinks", v)}
              value={form.value.gpxlinks ?? []}
              className="w-full"
            />
          ),
        },
        {
          key: "dropboxRequired",
          label: "reservations.fields.dropboxRequired",
          orientation: "horizontal",
          input: (_, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("dropboxRequired", v);
              }}
              checked={!!form.value.dropboxRequired}
            />
          ),
        },
        {
          visible: (f) => !!f.value.dropboxRequired,
          key: "dropboxLinks",
          label: "reservations.fields.dropboxLinks",
          input: (_, id) => (
            <TextList
              id={id}
              onChange={(v) => form.setValue("dropboxLinks", v)}
              value={form.value.dropboxLinks ?? []}
              className="w-full"
            />
          ),
        },
        {
          key: "feedbackRequired",
          label: "reservations.fields.feedbackRequired",
          orientation: "horizontal",
          input: (_, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("feedbackRequired", v);
              }}
              checked={!!form.value.feedbackRequired}
            />
          ),
        },
        {
          visible: (f) => !!f.value.feedbackRequired,
          key: "feedbackLinks",
          label: "reservations.fields.feedbackLinks",
          input: (_, id) => (
            <TextList
              id={id}
              onChange={(v) => form.setValue("feedbackLinks", v)}
              value={form.value.feedbackLinks ?? []}
              className="w-full"
            />
          ),
        },
        {
          key: "customLinks",
          label: "reservations.fields.customLinks",
          input: (_, id) => (
            <TextList
              id={id}
              onChange={(v) => form.setValue("customLinks", v)}
              value={form.value.customLinks ?? []}
              className="w-full"
            />
          ),
        },
        {
          key: "travefySent",
          label: "reservations.fields.travefySent",
          input: (_, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.travefySent}
              onChange={(v) => {
                form.setValue("travefySent", v);
              }}
              options={reservationTagsAsArray.map((rs) => ({
                value: rs,
                label: t(`reservations.tags.${rs}`),
              }))}
            />
          ),
        },
        {
          key: "rwgpsSent",
          label: "reservations.fields.rwgpsSent",
          input: (_, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.rwgpsSent}
              onChange={(v) => {
                form.setValue("rwgpsSent", v);
              }}
              options={reservationTagsAsArray.map((rs) => ({
                value: rs,
                label: t(`reservations.tags.${rs}`),
              }))}
            />
          ),
        },
        {
          key: "gpxSent",
          label: "reservations.fields.gpxSent",
          input: (_, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.gpxSent}
              onChange={(v) => {
                form.setValue("gpxSent", v);
              }}
              options={reservationTagsAsArray.map((rs) => ({
                value: rs,
                label: t(`reservations.tags.${rs}`),
              }))}
            />
          ),
        },
      ]}
    />
  );
}
